import { DEFAULT_THEME } from '../defaults/theme.defaults';
import type { ThemeConfig, ThemeTypographySize } from '../models/theme.model';
import { REPORT_FONT_FAMILY_OPTIONS } from '../../reports/models/report-theme-font-options';
import { normalizeThemeConfigCard } from './normalize-theme-card.util';

/**
 * Theme typography `size` entries are multipliers of `baseSize` in the app (see
 * {@link DEFAULT_THEME}). The API often stores absolute px values (e.g. 16 for md).
 * Values above this threshold are converted to multipliers.
 */
const TYPOGRAPHY_SIZE_MULTIPLIER_MAX = 3;

function normalizeFontToken(value: string): string {
	return value
		.replace(/^['"]+|['"]+$/g, '')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase();
}

function primaryFontToken(value: string): string {
	const primary = value.split(',')[0] ?? value;
	return normalizeFontToken(primary);
}

function parseBaseSizePx(baseSize: string | undefined): number {
	const parsed = parseFloat(baseSize ?? '');
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
}

function typographySizeNeedsNormalization(size: ThemeTypographySize): boolean {
	return Object.values(size).some(
		value =>
			Number.isFinite(value) && value > TYPOGRAPHY_SIZE_MULTIPLIER_MAX
	);
}

function normalizeTypographySizeValue(
	value: number,
	baseSizePx: number
): number {
	if (!Number.isFinite(value) || value <= 0) {
		return 1;
	}
	if (value > TYPOGRAPHY_SIZE_MULTIPLIER_MAX) {
		return value / baseSizePx;
	}
	return value;
}

function normalizeTypographySizeScale(
	size: ThemeTypographySize,
	baseSizePx: number
): ThemeTypographySize {
	return {
		xxs: normalizeTypographySizeValue(size.xxs, baseSizePx),
		xs: normalizeTypographySizeValue(size.xs, baseSizePx),
		sm: normalizeTypographySizeValue(size.sm, baseSizePx),
		md: normalizeTypographySizeValue(size.md, baseSizePx),
		lg: normalizeTypographySizeValue(size.lg, baseSizePx),
		xl: normalizeTypographySizeValue(size.xl, baseSizePx),
		xxl: normalizeTypographySizeValue(size.xxl, baseSizePx),
	};
}

function typographySizeEquals(
	left: ThemeTypographySize,
	right: ThemeTypographySize
): boolean {
	return (
		left.xxs === right.xxs &&
		left.xs === right.xs &&
		left.sm === right.sm &&
		left.md === right.md &&
		left.lg === right.lg &&
		left.xl === right.xl &&
		left.xxl === right.xxl
	);
}

/** Maps API labels or bare names to the full CSS `font-family` stack used in the theme editor. */
export function normalizeThemeFontFamily(
	fontFamily: string | undefined
): string {
	const raw = fontFamily?.trim();
	if (!raw) {
		return DEFAULT_THEME.typography.fontFamily;
	}

	const exact = REPORT_FONT_FAMILY_OPTIONS.find(
		option => option.value === raw || option.label === raw
	);
	if (exact) {
		return exact.value;
	}

	const byLabel = REPORT_FONT_FAMILY_OPTIONS.find(
		option => option.label.toLowerCase() === raw.toLowerCase()
	);
	if (byLabel) {
		return byLabel.value;
	}

	const rawPrimaryToken = primaryFontToken(raw);
	const byPrimaryToken = REPORT_FONT_FAMILY_OPTIONS.find(option => {
		if (normalizeFontToken(option.label) === rawPrimaryToken) {
			return true;
		}
		return primaryFontToken(option.value) === rawPrimaryToken;
	});
	if (byPrimaryToken) {
		return byPrimaryToken.value;
	}

	if (raw.includes(',') || raw.startsWith('"')) {
		return raw;
	}

	return raw;
}

/** Ensures `typography.size` uses multipliers so font CSS vars are not blown up. */
export function normalizeThemeConfigTypography(
	theme: ThemeConfig
): ThemeConfig {
	const typography = theme.typography ?? DEFAULT_THEME.typography;
	const size = typography.size ?? DEFAULT_THEME.typography.size;
	const fontFamily = normalizeThemeFontFamily(typography.fontFamily);

	let nextSize = size;
	if (typographySizeNeedsNormalization(size)) {
		const baseSizePx = parseBaseSizePx(typography.baseSize);
		nextSize = normalizeTypographySizeScale(size, baseSizePx);
	}

	const sizeChanged = !typographySizeEquals(size, nextSize);
	const fontChanged = fontFamily !== typography.fontFamily;

	if (!sizeChanged && !fontChanged) {
		return theme;
	}

	return {
		...theme,
		typography: {
			...typography,
			fontFamily,
			size: nextSize,
		},
	};
}

/** Full theme normalization for runtime/report canvas (card px units + typography). */
export function normalizeThemeConfig(theme: ThemeConfig): ThemeConfig {
	return normalizeThemeConfigTypography(normalizeThemeConfigCard(theme));
}

/** True when two loaded theme configs are equivalent for active preview purposes. */
export function isSameLoadedThemeConfig(
	current: ThemeConfig | null,
	next: ThemeConfig
): boolean {
	if (!current) {
		return false;
	}
	if (current === next) {
		return true;
	}
	if (current.themeId !== next.themeId) {
		return false;
	}
	return JSON.stringify(current) === JSON.stringify(next);
}
